import React from 'react';
import { Table, Space, Avatar, Typography, Badge, Button, Input, theme, Tag } from 'antd';
import { UserOutlined, SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { usePatients } from '../data/patient';

const { Title, Text } = Typography;
const { useToken } = theme;

const PatientTable = () => {
  const patients = usePatients();
  const { token } = useToken();
  const navigate = useNavigate();

  const patientColumns = [
    {
      title: 'Patient',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Avatar 
            size="large"
            style={{ 
              background: `linear-gradient(135deg, ${record.avatarColor} 0%, ${token.colorPrimaryHover} 100%)`,
              color: 'white',
              fontWeight: 'bold'
            }}
          >
            {text.charAt(0)}
          </Avatar>
          <div>
            <Text strong style={{ display: 'block', color: token.colorTextHeading }}>
              {text}
            </Text>
            <Text type="secondary" style={{ fontSize: '13px' }}>
              ID: {record.id}
            </Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Age',
      dataIndex: 'age',
      key: 'age',
      render: (age) => <Text>{age} years</Text>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Badge 
          color={status === 'Active' ? 'green' : 'gray'} 
          text={
            <Text style={{ 
              color: status === 'Active' ? token.green6 : token.colorTextSecondary,
              fontWeight: 500
            }}>
              {status}
            </Text>
          } 
        />
      )
    },
    {
      title: 'Last Visit',
      dataIndex: 'lastVisit',
      key: 'lastVisit',
      render: (text) => <Text type="secondary">{text}</Text>
    },
    {
      title: 'Next Appointment',
      dataIndex: 'appointment',
      key: 'appointment',
      render: (text) => (
        text === 'None' ? 
          <Text type="secondary">{text}</Text> : 
          <Text strong>{text}</Text>
      )
    },
    {
      title: 'Condition',
      dataIndex: 'condition',
      key: 'condition',
      render: (text) => (
        <Tag 
          color="blue" 
          style={{ 
            borderRadius: '12px',
            padding: '0 8px',
            background: token.blue1,
            borderColor: token.blue3,
            color: token.blue7
          }}
        >
          {text}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            type="text" 
            shape="circle" 
            icon={<UserOutlined style={{ color: token.colorTextSecondary }} />} 
            style={{ width: '32px', height: '32px' }}
          />
          <Button 
            type="primary" 
            size="small" 
            shape="round"
            style={{ transition: 'all 0.3s' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = token.colorPrimaryHover}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = token.colorPrimary}
          >
            View
          </Button>
        </Space>
      )
    }
  ];

  return (
    <>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '24px 24px 16px',
        borderBottom: `1px solid ${token.colorBorderSecondary}`
      }}>
        <Title level={5} style={{ 
          color: token.colorTextHeading, 
          margin: 0,
          fontWeight: 700,
          fontSize: '16px'
        }}>
          Patient Records
        </Title>
        <Space>
          <Input 
            placeholder="Search patients..." 
            prefix={<SearchOutlined style={{ color: token.colorTextSecondary }} />}
            style={{ 
              width: 250,
              borderRadius: '12px',
              background: token.colorBgElevated,
              borderColor: token.colorBorder,
              boxShadow: token.boxShadowTertiary
            }}
          />
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            style={{ 
              borderRadius: '12px',
              fontWeight: 500
            }}
            onClick={() => navigate('/patient/add-patient')}
          >
            Add Patient
          </Button>
        </Space>
      </div>
      <Table 
        columns={patientColumns}
        dataSource={patients}
        rowKey="id"
        style={{ padding: '0 24px' }}
        pagination={{
          position: ['bottomRight'],
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (total) => `Showing ${patients.length} of ${total} patients`,
          style: { padding: '0 24px 24px' }
        }}
      />
    </>
  );
};

export default PatientTable;